import React, { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { IntercomProvider as ReactIntercomProvider, useIntercom } from "react-use-intercom";
import { analyticsService } from "../services/analyticsService";
import configService from "../services/configService";
import formService from "../services/formService";
import httpService from "../services/httpService";
import { intercomService } from "../services/intercomService";
import { IntercomSession } from "../services/intercomSession";
import { clearIntercomIdentity, hasIntercomInstance, hasIntercomSession, rebootIntercom, waitForIntercom } from "../services/intercomBrowser";
import { trackingService } from "../services/trackingService";
import { TrackingEvents } from "../services/trackingEvents";
import { extractUTMParams } from "../utils/analytics";
import { isInternalUser } from "../utils/internalUsers";
import { isCustomerViewBootProfile } from "../utils/adminCustomerViewBoot";
import { useAuth } from "./AuthContext";
import { useTheme } from "./ThemeContext";
import { SupportChatContext } from "./SupportChatContext";

const getBrowserMetadata = () => {
  if (typeof window === "undefined") return {};

  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(", ") || navigator.language,
    platform: navigator.platform,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || undefined,
    onLine: navigator.onLine,
  };
};

const IntercomManager: React.FC<{ children: ReactNode; appId: string }> = ({ children, appId }) => {
  const sdk = useIntercom();
  const sdkRef = useRef(sdk);
  sdkRef.current = sdk;
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const { theme, isDarkMode } = useTheme();
  const [sessionRevision, setSessionRevision] = useState(0);
  const markerKey = `plutus_intercom_identified_${appId}`;
  const session = useMemo(() => {
    let prepared = false;
    let active = false;
    return new IntercomSession({
      ready: waitForIntercom,
      boot: (props, reboot) => {
        // This flag contains no user ID or token. It prevents restoring an identified
        // cookie as a visitor after a closed tab or a missed cross-tab logout.
        if (!prepared) {
          let previouslyIdentified = true;
          try { previouslyIdentified = localStorage.getItem(markerKey) !== "guest"; } catch { /* Fail closed if storage is blocked. */ }
          if (previouslyIdentified) clearIntercomIdentity(appId);
          prepared = true;
        }
        try { localStorage.setItem(markerKey, props.intercomUserJwt ? "identified" : "guest"); } catch { /* Identity remains memory-only. */ }
        if (reboot) rebootIntercom(appId, props);
        else sdkRef.current.boot(props);
        active = true;
      },
      update: props => sdkRef.current.update(props),
      shutdown: () => { if (active) sdkRef.current.shutdown(); active = false; },
      clearIdentity: () => {
        clearIntercomIdentity(appId);
        try { localStorage.removeItem(markerKey); } catch { /* Cookie cleanup still runs. */ }
      },
      hasSession: () => hasIntercomSession(appId),
      show: () => sdkRef.current.show(),
      hide: () => { if (active) sdkRef.current.hide(); },
      trackEvent: (name, metadata) => sdkRef.current.trackEvent(name, metadata),
    }, undefined, (outcome, durationMs, refresh) => {
      try {
        trackingService.track(TrackingEvents.SUPPORT_SESSION_OUTCOME, { outcome, duration_ms: durationMs, refresh, platform: "web" });
      } catch { /* Observability never blocks support. */ }
    });
  }, [appId, markerKey]);
  const status = useSyncExternalStore(session.subscribe, session.getStatus);
  const generation = httpService.getActorSessionState().generation;
  const actorId = auth.isAuthenticated ? auth.actorUserId || auth.user?.id || null : null;
  const environment = configService.getEnvironment() || "unknown";
  const ownerKey = `${environment}:${appId}:${generation}:${actorId || "guest"}`;
  const attributes = useMemo(() => {
    const utm = extractUTMParams();
    const user = auth.isAuthenticated ? auth.user : null;
    const daysSince = (date: Date | string) => Math.floor(Math.abs(Date.now() - new Date(date).getTime()) / 86_400_000);
    return {
      ...getBrowserMetadata(), environment, theme, darkMode: isDarkMode,
      browser: navigator.userAgent, devicePixelRatio: window.devicePixelRatio,
      ...(user ? {
        emailVerified: user.emailVerified, hasProfileImage: !!user.image,
        is_internal: isInternalUser(user.email), roles: user.roles?.join(", ") || "INVESTOR",
        roleDetails: user.userRoles?.map(role => `${role.role} (since ${new Date(role.createdAt).toLocaleDateString()})`).join("; "),
        primaryRole: user.roles?.[0] || "INVESTOR", isAdmin: user.roles?.includes("ADMIN") || false,
        isProvider: user.roles?.includes("PROVIDER") || false, isInvestor: user.roles?.includes("INVESTOR") || false,
        providerId: user.providerId || undefined,
        providerName: user.provider?.name || user.userProviders?.[0]?.providers?.name,
        allProviders: user.userProviders?.map(provider => provider.providers.name).join(", "),
        providerCount: user.userProviders?.length || 0,
        accountAge: user.createdAt ? daysSince(user.createdAt) : undefined,
        daysSinceLastUpdate: user.updatedAt ? daysSince(user.updatedAt) : undefined,
        lastUpdated: user.updatedAt ? new Date(user.updatedAt).toISOString() : undefined,
      } : { authenticated: false }),
      utmSource: utm.utm_source, utmMedium: utm.utm_medium, utmCampaign: utm.utm_campaign,
      utmContent: utm.utm_content, utmTerm: utm.utm_term,
      ga_client_id: analyticsService.getGAClientId() || undefined,
    };
  }, [environment, theme, isDarkMode, auth.isAuthenticated, auth.user]);

  useLayoutEffect(() => {
    const unsubscribe = httpService.subscribeActorSession(() => {
      // Invalidate pending work synchronously, before React paints or logout awaits I/O.
      session.suspend();
      intercomService.reset();
      setSessionRevision(revision => revision + 1);
    });
    return () => { unsubscribe(); intercomService.reset(); session.dispose(); };
  }, [session]);

  useLayoutEffect(() => {
    const blocked = auth.isLoading || httpService.getActorSessionState().transitioning
      || (auth.isAuthenticated && !actorId)
      || auth.isAuthenticated !== !!httpService.getAuthTokenPair();
    session.setOwner(blocked ? null : {
      key: ownerKey,
      actorId,
      isCurrent: () => {
        const currentAuth = authRef.current;
        const currentSession = httpService.getActorSessionState();
        const currentActor = currentAuth.isAuthenticated ? currentAuth.actorUserId || currentAuth.user?.id || null : null;
        return !currentAuth.isLoading && !currentSession.transitioning
          && currentSession.generation === generation && actorId === currentActor
          && !httpService.hasUnadoptedExternalAuthTokenChange()
          && (!actorId || !!httpService.getAuthTokenPair());
      },
    }, attributes);
  }, [session, ownerKey, generation, actorId, auth.isLoading, auth.isAuthenticated, attributes, sessionRevision]);

  useEffect(() => {
    if (status !== "guest" && status !== "verified") {
      intercomService.reset();
      return;
    }
    const current = session.captureOwner();
    intercomService.initialize(
      props => { if (current()) session.update(props); },
      (name, metadata) => { if (current()) session.trackEvent(name, metadata); },
      () => current() && !!actorId && !authRef.current.isAccountSwitching
        && (!authRef.current.effectiveUserId || authRef.current.effectiveUserId === actorId)
    );
    return () => intercomService.reset();
  }, [session, ownerKey, status, actorId]);

  useEffect(() => { session.update({ customAttributes: attributes }); }, [session, attributes]);

  useEffect(() => {
    const resume = () => { if (document.visibilityState === "visible") session.resume(); };
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => { window.removeEventListener("focus", resume); document.removeEventListener("visibilitychange", resume); };
  }, [session]);

  // The default launcher bypasses our buttons. Validate its session on open too.
  useEffect(() => { if (sdk.isOpen) session.resume(); }, [sdk.isOpen, session]);

  const syncedOwner = useRef<string | null>(null);
  useEffect(() => {
    if (status !== "verified" || !actorId || syncedOwner.current === ownerKey
      || isCustomerViewBootProfile() || auth.isAccountSwitching
      || (auth.effectiveUserId && auth.effectiveUserId !== actorId)) return;
    syncedOwner.current = ownerKey;
    const current = session.captureOwner();
    let cancelled = false;
    void formService.getQuestionnaireProgress({ category: "onboarding", excludeSubcategory: "deleted" }).then(({ questions, progress }) => {
      if (cancelled || !current()) return;
      if (questions.length > 0 && progress.answeredQuestions > 0) {
        const responses = Array.from(progress.responses.entries()).map(([questionId, answer]) => ({
          id: 0, questionId, userId: actorId, answer, createdDateUtc: "", createdBy: "", updatedDateUtc: "", updatedBy: "",
        }));
        intercomService.syncAllQuestionnaireResponses(questions, responses);
      }
      intercomService.trackLoginSuccess();
    }).catch(() => { /* Questionnaire enrichment never blocks support. */ });
    return () => { cancelled = true; };
  }, [session, status, actorId, ownerKey, auth.isAccountSwitching, auth.effectiveUserId]);

  const actions = useMemo(() => ({
    show: () => { if (session.owns(ownerKey)) session.show(); },
    hide: () => { if (session.owns(ownerKey)) session.hide(); },
    update: (props: Parameters<IntercomSession["update"]>[0]) => { if (session.owns(ownerKey)) session.update(props); },
  }), [session, ownerKey]);
  const chat = useMemo(() => ({ status, isOpen: sdk.isOpen && (status === "guest" || status === "verified"),
    ...actions }), [actions, status, sdk.isOpen]);
  return <SupportChatContext.Provider value={chat}>
    {children}
    {status === "support-error" && <div role="status" className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 shadow-lg dark:border-red-800 dark:bg-rp-surface dark:text-red-400">
      <p>Support chat is unavailable. Please try again.</p>
      <button type="button" onClick={session.show} className="mt-2 rounded border border-current px-3 py-1 font-semibold focus-visible:outline focus-visible:outline-2">Retry support chat</button>
    </div>}
  </SupportChatContext.Provider>;
};

export const IntercomProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const appId = configService.getIntercomAppId();
  if (!appId) return <>{children}</>;
  return <ReactIntercomProvider appId={appId} autoBoot={false} shouldInitialize={!hasIntercomInstance()}>
    <IntercomManager appId={appId}>{children}</IntercomManager>
  </ReactIntercomProvider>;
};

export default IntercomProvider;
